import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';

import type { TEntityOption } from '@/components/filter/types';

import type { TStationListItem } from '@/types/station';

import { lookupQuery } from '@/queries/lookup';
import { OPENING_SITES_PARAMS, stationListQuery } from '@/queries/station';
import { storage } from '@/storage';
import { tokenUtils } from '@/tokenUtils';

import {
  PORTAL_FILTER_STORAGE_KEY,
  clampPortalDateRange,
  getDefaultPortalDateRange,
  getDefaultStationIds,
  mergeStationOptions,
  normalizeStationPick,
  toIsoEnd,
  toIsoStart,
  toStationOption,
  uniqueStationIds,
} from './portalFilterUtils';
import type { TPortalFilter, TPortalFilterContext } from './types';

const PortalFilterContext = createContext<TPortalFilterContext | null>(null);

const persistPortalFilter = (filter: TPortalFilter) => {
  storage.setItem(PORTAL_FILTER_STORAGE_KEY, filter);
  return filter;
};

/**
 * The portal filter at page load: what the operator saved (sites, dates, site names), with the
 * dates clamped to the allowed window or defaulted to last month.
 *
 * The saved site pick is kept as is here (ids, or the `allSites` flag). Whether it is replaced by
 * the default sites or turned into "All sites" is decided once the opening call is in (see the
 * resolve effect in `PortalFilterProvider`), because that needs the site total.
 * Sign-out removes this storage key (`clearPersistentFilters`), so a new sign-in starts with no
 * saved pick.
 */
const createInitialPortalFilter = (): TPortalFilter => {
  const stored = storage.getItem<TPortalFilter>(PORTAL_FILTER_STORAGE_KEY) ?? {};
  const hasDates = Boolean(toIsoStart(stored.startDate) && toIsoEnd(stored.endDate));
  const dates = hasDates
    ? clampPortalDateRange(stored.startDate, stored.endDate)
    : getDefaultPortalDateRange();

  return persistPortalFilter({
    ...stored,
    stationId: uniqueStationIds(stored.stationId),
    allSites: stored.allSites,
    stations: stored.stations,
    startDate: dates.startDate,
    endDate: dates.endDate,
  });
};

type TStationsPage = { result: TStationListItem[]; totalHits: number };

// ---------------------------------------------------------------------------------------------
// RETIRED: full-list design. The provider used to load the partner's whole site list in the
// background (pages of 100 in parallel, `STATIONS_PAGE_SIZE`) so the picker could show "All Sites"
// as every site ticked and filter locally. That took 25 s and more for big partners, so the owner
// replaced it with the 12 opening sites, an "All Sites" row and on-demand loads. Kept below,
// commented out, in case the design returns.
// ---------------------------------------------------------------------------------------------
// /** Sites per call when loading the partner's site list. */
// const STATIONS_PAGE_SIZE = 100;
//
// /**
//  * Sites asked for by the opening call that lets the app open. The backend costs about 130 ms per
//  * site returned, so 5 sites cost about 2 s before the app opens (about 0.5 s more than 1 site).
//  * Why 5: the My Sites carousel shows its first 5 slides at once, without waiting for the
//  * background pages. A larger number delays the whole portal; a smaller one makes the carousel
//  * show its loader on slides 2 to 5.
//  */
// const OPENING_SITES = 5;
//
// type TStationsPage = { result: TStationListItem[]; totalHits: number };
//
// /**
//  * One page of the site list. The backend costs about 130 ms per site returned, so the app opens
//  * with a short call (`OPENING_SITES`) and loads full pages (`STATIONS_PAGE_SIZE`) only in the
//  * background.
//  */
// const requestStationsPage = (page: number, size = STATIONS_PAGE_SIZE): Promise<TStationsPage> =>
//   getStations({ page, size, filter: {} }).then(({ data }) => data.data);
//
// /** Background pages that arrived so far, by zero-based page number (pages of `STATIONS_PAGE_SIZE`). */
// type TSitePages = Record<number, TStationListItem[]>;
//
// /**
//  * The shared site list: the sites of the arrived pages in page order, de-duplicated by id.
//  * Until page 0 arrives, `openingSites` (the opening call) hold positions 0 to 4. Pages can arrive
//  * in any order, so a list built before every page arrived has gaps; `siteAt` is the position-exact read.
//  */
// const buildSiteList = (
//   pages: TSitePages,
//   openingSites: TStationListItem[] = [],
// ): TStationListItem[] => {
//   const byId = new Map<string, TStationListItem>();
//   if (!pages[0]) {
//     openingSites.forEach((site) => byId.set(site.id, site));
//   }
//   Object.keys(pages)
//     .map(Number)
//     .sort((a, b) => a - b)
//     .forEach((page) => pages[page].forEach((site) => byId.set(site.id, site)));
//   return [...byId.values()];
// };
//
// /** True when `ids` names every site in `sites` (order does not matter). */
// const coversEverySite = (ids: string[] | undefined, sites: TStationListItem[]) => {
//   if (!ids?.length || !sites.length) {
//     return false;
//   }
//   const picked = new Set(ids);
//   return sites.every(({ id }) => picked.has(id));
// };

/**
 * Shared site scope of the portal (sites, dates, site names) plus the site data the pickers and
 * the My Sites card start from.
 *
 * Load: one opening call (`OPENING_SITES` = 12, the saved `stationListQuery`) gives the first 12
 * sites and the site total, and refreshes when a station is saved or created. The
 * rest of the partner's sites are never loaded here: the picker searches the backend by what the
 * operator types and the My Sites card loads one slide at a time.
 */
export const PortalFilterProvider = ({ children }: { children: React.ReactNode }) => {
  const [portalFilter, setPortalFilter] = useState<TPortalFilter>(createInitialPortalFilter);
  // True once the starting pick is decided (the resolve effect below sets it, once).
  const [pickResolved, setPickResolved] = useState(false);
  // Whether a token existed at page load. Read once, like the old opening effect did: with no token
  // there is no opening call and the app opens with no sites known.
  const [hasToken] = useState(() => Boolean(tokenUtils.getLocallyStoredToken()));
  // Whether the operator had a saved site pick at page load (ids, or "All sites"). Fixed at the
  // first render: it decides if the default sites are written as the pick.
  const hadSavedPick = useRef(Boolean(portalFilter.stationId?.length || portalFilter.allSites));

  // The `partner_portal` lookup carries the default-sites row. React Query shares one saved copy
  // (`lookupQuery`, never refreshed) among its callers (for example `useVisitsMinDwell`), but not
  // with `PartnerRoleProvider`, which calls the service directly: that costs one more small
  // request, started with the opening call, so it adds no waiting time.
  // `lookup.isLoading` is true only while there is no answer yet; it turns false on success and
  // on failure (after the one retry), like the old `loading` flag.
  const lookup = useQuery(lookupQuery({ category: 'partner_portal' }));

  /**
   * The opening call (`OPENING_SITES` sites, about 1.6 s): the first 12 sites (-> `openingSites`,
   * `firstSite`) and the site total (-> `totalSites`, `soleStationId`).
   * Read from the saved station list query, never copied into state. A station save or create
   * (W1 / W3) marks it old, so a rename, a new site and the site total refresh while the operator
   * is signed in (before, they showed up only at the next page load).
   * No token: the call is not made (`enabled`).
   */
  const opening = useQuery({ ...stationListQuery(OPENING_SITES_PARAMS), enabled: hasToken });
  // `null` (the backend sent no `data`) counts as no answer, like `undefined` did before the
  // query functions returned `null` for an empty answer (see `unwrapData`).
  const openingData: TStationsPage | undefined = opening.data ?? undefined;
  const openingSites = useMemo(() => openingData?.result ?? [], [openingData]);
  const totalSites = openingData?.totalHits ?? 0;
  const openingLoaded = Boolean(openingData);
  const soleStationId =
    openingData?.totalHits === 1 && openingData.result[0]?.id
      ? openingData.result[0].id
      : undefined;
  // The opening call gave no sites: it failed for good (after its one retry), or it succeeded with
  // an empty answer (`null`). Either way nothing was loaded and pages must not wait for it.
  const openingFailed = (opening.isError || opening.isSuccess) && !openingData;
  // Read inside `setStations` without re-creating it: a pick of every site becomes "All sites"
  // when its count equals the site total. Assigned while rendering: it only mirrors `totalSites`.
  const totalSitesRef = useRef(0);
  totalSitesRef.current = totalSites;

  // Pages wait for this. It is true once the starting pick is decided, or at once when the opening
  // call cannot give sites (no token, or it failed): the app then opens with no sites known and the
  // saved pick (if any) is kept. If the call later succeeds (the query refreshes on window focus),
  // the resolve effect below still decides the pick once.
  const stationsResolved = pickResolved || !hasToken || openingFailed;

  // First site of the backend list, from the opening call.
  const firstSite: TStationListItem | undefined = openingSites[0];

  const openingStations = useMemo(() => openingSites.map(toStationOption), [openingSites]);

  /**
   * The sites the portal starts on with no saved pick, and where the picker's Reset goes back to.
   * Derived, not stored: it follows the opening sites and the lookup, so it is right whenever
   * both are in (see `getDefaultStationIds` for the rules). While the lookup is still loading it
   * is the first site only; the resolve effect waits for the lookup before it uses this value.
   */
  const defaultStationIds = useMemo(
    () => getDefaultStationIds(lookup.data, openingSites),
    [lookup, openingSites],
  );

  /**
   * Decides the starting pick once, then lets pages load (`stationsResolved`).
   *
   * Precedence: a saved pick wins; else the lookup default sites; else the first site only.
   * - Waits for the opening call (needs the site total and the opening sites) and for the lookup to
   *   finish (success or failure), always. Even with a saved pick: `defaultStationIds` (so
   *   `firstSiteId` of single-site pages while the pick is "All sites", and Reset) depends on the
   *   lookup, and it must not change after pages start loading.
   * - Saved ids whose unique count equals the site total become "All sites" (an old saved list of
   *   every site stops sending hundreds of ids). A saved "All sites" (`allSites`) is kept. Any
   *   other saved ids are kept as they are.
   * - No saved pick: the default sites are written as the pick (and saved, like any pick). A
   *   default that names every site of the partner is stored as "All sites".
   * - `stationsResolved` is set in the same batch as the pick (this runs inside an effect), so a
   *   page that waits for it never sees the pick before it is final.
   * Runs once: `pickResolved` guards it. A failed or token-less opening call sets
   * `stationsResolved` without it (see above) and the effect waits for sites.
   */
  useEffect(() => {
    if (pickResolved || !openingLoaded) {
      return;
    }

    if (lookup.isLoading) {
      return;
    }

    setPortalFilter((prev) => {
      const savedIds = uniqueStationIds(prev.stationId);

      if (savedIds) {
        return normalizeStationPick(savedIds, totalSites)
          ? prev
          : persistPortalFilter({ ...prev, stationId: undefined, allSites: true });
      }

      if (hadSavedPick.current) {
        return prev;
      }

      const stationId = normalizeStationPick(defaultStationIds, totalSites);
      return persistPortalFilter({
        ...prev,
        stationId,
        allSites: stationId ? undefined : true,
      });
    });
    setPickResolved(true);
  }, [pickResolved, openingLoaded, lookup.isLoading, defaultStationIds, totalSites]);

  // The pick that "All sites" falls back to on single-site pages: the first default site.
  const firstSiteId =
    uniqueStationIds(portalFilter.stationId)?.[0] ?? defaultStationIds[0] ?? firstSite?.id;

  /**
   * Narrows the portal to `stationId`. A pick that names every site (its count equals the site
   * total) is stored as "All sites" (no ids), so requests stay short and a later new site is
   * included automatically. `undefined` or no ids is "All sites" too; either way `allSites` is
   * saved so the next page load keeps it instead of starting on the default sites.
   */
  const setStations = useCallback((stationId: string[] | undefined, stations?: TEntityOption[]) => {
    const ids = normalizeStationPick(stationId, totalSitesRef.current);

    setPortalFilter((prev) =>
      persistPortalFilter({
        ...prev,
        stationId: ids,
        allSites: ids ? undefined : true,
        stations: stations ? mergeStationOptions(prev.stations ?? [], stations) : prev.stations,
      }),
    );
  }, []);

  // RETIRED: full-list design (see the note above `OPENING_SITES`). Old effect, list builders and
  // `setStations`, kept for reference.
  // export const PortalFilterProvider = ({ children }: { children: React.ReactNode }) => {
  //   const [portalFilter, setPortalFilter] = useState<TPortalFilter>(createInitialPortalFilter);
  //   const [stationsResolved, setStationsResolved] = useState(false);
  //   const [soleStationId, setSoleStationId] = useState<string | undefined>();
  //   // First sites (up to `OPENING_SITES`) and site count, from the opening call that opens the app.
  //   const [openingSites, setOpeningSites] = useState<TStationListItem[]>([]);
  //   const [totalSites, setTotalSites] = useState(0);
  //   // The partner's site list, loaded once per page load and shared by the picker and the My Sites
  //   // card: background pages are added here as each one arrives.
  //   const [sitePages, setSitePages] = useState<TSitePages>({});
  //   const [allStationsStatus, setAllStationsStatus] =
  //     useState<TPortalFilterContext['allStationsStatus']>('loading');
  //   // Read inside `setStations` without re-creating it: a pick of every site becomes "All sites"
  //   // only when the full list is known.
  //   const fullSiteListRef = useRef<TStationListItem[] | null>(null);
  //
  //   /**
  //    * Opens the app fast, then loads the partner's site list in the background.
  //    *
  //    * - An opening call (`OPENING_SITES` sites, about 2 s) gives `totalHits` (-> `totalSites`,
  //    *   `soleStationId`), the first site (-> `firstSite`, the `firstSiteId` fallback) and the first
  //    *   5 sites (-> `openingSites`, positions 0 to 4 of the shared list until page 0 arrives). The
  //    *   app opens right after it (`stationsResolved`): "All sites" needs no id list. A partner with
  //    *   fewer sites gets fewer; the sole-site rule still reads `totalHits === 1`.
  //    * - The full list loads next, pages of 100 in parallel. The page count comes from `totalHits`,
  //    *   so there is no open-ended loop; a short or empty page just adds fewer sites. Each page is
  //    *   added to the shared list the moment it arrives (`sitePages`), in any order.
  //    * - When every page arrived (`allStationsStatus` 'loaded'): a saved pick that names every site becomes "All sites", so an
  //    *   old saved list stops sending hundreds of ids.
  //    * - A failing call marks the list 'failed' and keeps what arrived. The selection is untouched.
  //    * A site added while the operator is signed in shows up at the next page load.
  //    */
  //   useEffect(() => {
  //     let relevant = true;
  //
  //     const loadStations = async () => {
  //       if (!tokenUtils.getLocallyStoredToken()) {
  //         if (relevant) {
  //           setStationsResolved(true);
  //           setAllStationsStatus('failed');
  //         }
  //         return;
  //       }
  //
  //       let first: TStationsPage;
  //       try {
  //         first = await requestStationsPage(0, OPENING_SITES);
  //       } catch (error) {
  //         handleError(error, 'UNHANDLED_RESPONSE');
  //         if (relevant) {
  //           setSoleStationId(undefined);
  //           setAllStationsStatus('failed');
  //           setStationsResolved(true);
  //         }
  //         return;
  //       }
  //
  //       if (!relevant) {
  //         return;
  //       }
  //
  //       setSoleStationId(first.totalHits === 1 && first.result[0]?.id ? first.result[0].id : undefined);
  //       setOpeningSites(first.result);
  //       setTotalSites(first.totalHits);
  //       setStationsResolved(true);
  //
  //       const pageCount = Math.ceil(first.totalHits / STATIONS_PAGE_SIZE);
  //
  //       try {
  //         const arrived: TSitePages = {};
  //
  //         await Promise.all(
  //           Array.from({ length: pageCount }, (_, page) =>
  //             requestStationsPage(page).then(({ result }) => {
  //               arrived[page] = result;
  //               if (relevant) {
  //                 setSitePages((prev) => ({ ...prev, [page]: result }));
  //               }
  //             }),
  //           ),
  //         );
  //
  //         if (!relevant) {
  //           return;
  //         }
  //
  //         const sites = buildSiteList(arrived);
  //         fullSiteListRef.current = sites;
  //         setAllStationsStatus('loaded');
  //         setPortalFilter((prev) =>
  //           coversEverySite(prev.stationId, sites)
  //             ? persistPortalFilter({ ...prev, stationId: undefined })
  //             : prev,
  //         );
  //       } catch (error) {
  //         handleError(error, 'UNHANDLED_RESPONSE');
  //         if (relevant) {
  //           setAllStationsStatus('failed');
  //         }
  //       }
  //     };
  //
  //     loadStations();
  //
  //     return () => {
  //       relevant = false;
  //     };
  //   }, []);
  //
  //   // First site of the backend list, from the opening call; stays set after page 0 arrives.
  //   const firstSite: TStationListItem | undefined = openingSites[0];
  //
  //   const allSites = useMemo(() => buildSiteList(sitePages, openingSites), [sitePages, openingSites]);
  //
  //   const allStations = useMemo(() => allSites.map(toStationOption), [allSites]);
  //
  //   /** Site at a zero-based position of the backend list; undefined until its page arrives. */
  //   const siteAt = useCallback(
  //     (index: number): TStationListItem | undefined =>
  //       sitePages[Math.floor(index / STATIONS_PAGE_SIZE)]?.[index % STATIONS_PAGE_SIZE] ??
  //       (sitePages[0] ? undefined : openingSites[index]),
  //     [sitePages, openingSites],
  //   );
  //
  //   const firstSiteId = uniqueStationIds(portalFilter.stationId)?.[0] ?? firstSite?.id;
  //
  //   /**
  //    * Narrows the portal to `stationId`. A pick that names every site (full list known) is stored as
  //    * "All sites" (no ids), so requests stay short and a later new site is included automatically.
  //    */
  //   const setStations = useCallback((stationId: string[] | undefined, stations?: TEntityOption[]) => {
  //     const fullList = fullSiteListRef.current;
  //     const ids = fullList && coversEverySite(stationId, fullList) ? undefined : stationId;
  //
  //     setPortalFilter((prev) =>
  //       persistPortalFilter({
  //         ...prev,
  //         stationId: uniqueStationIds(ids),
  //         stations: stations ? mergeStationOptions(prev.stations ?? [], stations) : prev.stations,
  //       }),
  //     );
  //   }, []);

  const setDateRange = useCallback((startDate?: string, endDate?: string) => {
    const dates = clampPortalDateRange(startDate, endDate);
    setPortalFilter((prev) =>
      persistPortalFilter({
        ...prev,
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    );
  }, []);

  /**
   * Saves the names (and addresses) of sites the operator has seen, so the picker chips can show
   * a saved pick without asking the backend.
   * What: merges `stations` into the saved list; for a site id that is already saved, the new
   * entry wins, so a renamed site (or a changed address) updates its chip.
   * Why the compare checks title and details, not only ids: with ids only, a rename was ignored and
   * the chip kept the old name until the operator cleared the saved selection.
   * Edge: nothing is written (same state object returned) when the merged list equals the saved one.
   */
  const persistPortalStations = useCallback((stations: TEntityOption[]) => {
    setPortalFilter((prev) => {
      const prevStations = prev.stations ?? [];
      const merged = mergeStationOptions(prevStations, stations);

      if (
        merged.length === prevStations.length &&
        merged.every((station) =>
          prevStations.some(
            (item) =>
              item.id === station.id &&
              item.title === station.title &&
              item.details === station.details,
          ),
        )
      ) {
        return prev;
      }

      return persistPortalFilter({
        ...prev,
        stations: merged,
      });
    });
  }, []);

  /**
   * "Clear all" on list headers: back to "All sites" and the default dates. `allSites` is saved
   * so the next page load keeps "All sites" instead of starting on the default sites.
   */
  const clearPortalFilter = useCallback(() => {
    setPortalFilter((prev) =>
      persistPortalFilter({
        stations: prev.stations,
        stationId: undefined,
        allSites: true,
        ...getDefaultPortalDateRange(),
      }),
    );
  }, []);

  const value = useMemo(
    () => ({
      portalFilter,
      setStations,
      setDateRange,
      persistPortalStations,
      clearPortalFilter,
      stationsResolved,
      soleStationId,
      openingSites,
      openingStations,
      defaultStationIds,
      firstSite,
      totalSites,
      firstSiteId,
    }),
    [
      portalFilter,
      setStations,
      setDateRange,
      persistPortalStations,
      clearPortalFilter,
      stationsResolved,
      soleStationId,
      openingSites,
      openingStations,
      defaultStationIds,
      firstSite,
      totalSites,
      firstSiteId,
    ],
  );

  return <PortalFilterContext.Provider value={value}>{children}</PortalFilterContext.Provider>;
};

export const usePortalFilter = (): TPortalFilterContext => {
  const context = useContext(PortalFilterContext);

  if (!context) {
    throw new Error('usePortalFilter must be used within PortalFilterProvider');
  }

  return context;
};

export const useOptionalPortalFilter = () => useContext(PortalFilterContext);
